/**
 * One field, the column it is reading from, and how that pairing came about.
 *
 * Extracted so the screen and the dialog that guards it read the same answer.
 * The guard - "a required field with no column cannot go forward" - is only
 * trustworthy if the thing it counts is the thing the person is looking at.
 */
import {
  Mapping,
  MappingEntry,
  MappingHow,
  TransferSchema,
  UnwritableColumn,
} from '../../services/table-import-export.service';

export interface MappingRow {
  field: string;
  label: string;
  required: boolean;
  writable: boolean;
  how: MappingHow;
  /** The column now chosen for it, after any change made on screen. */
  header: string | null;
}

/**
 * ONE LINE PER FIELD OF THIS TABLE, and where it reads from.
 *
 * The third keying this screen has had, and the direction is the user\'s
 * ruling of 2026-08-31: the table\'s own fields on the left, the file on the
 * right, an arrow between them showing which way the data moves. A field\'s
 * source is ONE choice - a column of the file, the table\'s default, a typed
 * value for every row, or nothing - so the four handlings that used to live
 * in a separate "missing fields" section are this same dropdown; a field with
 * no column is not a different kind of row, it is a row whose source is not a
 * column.
 */
export interface FieldRow {
  field: string;
  label: string;
  required: boolean;
  /** What the table falls back to when nothing else is chosen. Null = the
   *  field simply arrives empty; the import invents nothing. */
  defaultValue: string | null;
  /** How the current column pairing came about, for the note. Null when the
   *  source is not a column. */
  how: MappingHow | null;
  source: FieldSource;
  /** On source `column` or `default`: the file column read from. */
  header: string | null;
  /**
   * THE COLUMN THE SYSTEM WORKED OUT for this field, whatever has been chosen
   * on top of it since. Null when it matched nothing.
   *
   * This is what `default` means as a source, so it has to survive every
   * later choice: "put this back to what the system decided" is only offered
   * while we still know what that was.
   */
  auto: string | null;
  /** On source `value`: the string put on every row. */
  value: string;
  /** A read-only field offered for override (Score, Origin...). Its "blank"
   *  means not imported, which is a different sentence from arriving empty. */
  overridable: boolean;
  /** Of those, the ones the system recalculates - an imported value lasts
   *  until the next run, and the row says so before the choice. */
  recalculated: boolean;
}

/**
 * Where a field's value comes from. Exactly one of four, per field.
 *
 *   column  - a column of the file, chosen by hand
 *   default - the column the SYSTEM matched for this field. Not the table's
 *             default VALUE, which it meant until 2026-09-02: on a screen
 *             whose whole subject is which column feeds which field, the
 *             default worth offering is the machine's own answer, and the
 *             one thing a person needs after overriding it is the way back.
 *             The table's fallback value is what `blank` produces, and the
 *             control says so - nothing was lost in the move, because the
 *             two were one message on the wire already: a field with no
 *             column and no constant is where the table's default applies.
 *   blank   - nothing from the file. The table fills the field with its own
 *             default where it has one, and leaves it empty where it does not
 *   value   - one typed string, on every row
 */
export type FieldSource = 'column' | 'default' | 'blank' | 'value';

/**
 * Whether this row feeds its field from a column of the file - by hand or by
 * the system's own match, which are the same message once posted.
 *
 * Shared because two places have to agree about it and drifted when they did
 * not: what gets POSTED as the mapping, and which of the file's columns count
 * as read. A column read under `default` that only one of them knew about
 * would be imported and listed as unimported at the same time.
 */
export function readsAColumn(row: FieldRow): boolean {
  return (row.source === 'column' || row.source === 'default')
    && row.header !== null;
}

/**
 * Every field a person can decide about: the writable ones, then the
 * overridable read-only ones under the same roof - they are fields of this
 * table too, and a second list would be a second thing to learn.
 *
 * `held` carries answers already given, so a re-read of the file does not
 * throw away what somebody typed - or which column they had pointed a field
 * at before they tried something else.
 *
 * `mapping` is the file and the system's own reading of it: its columns, so a
 * remembered column the file in hand does not carry is dropped rather than
 * posted; and its entries, which are the system's matches and therefore what
 * the `default` source resolves to. `rows` cannot answer the second - once
 * anything is settled by hand it is built from the choice instead, and the
 * machine's original answer is no longer in it.
 */
export function fieldRows(
  schema: TransferSchema,
  rows: readonly MappingRow[],
  choice: Readonly<Record<string, string>>,
  settled: boolean,
  held: ReadonlyMap<string, FieldRow>,
  mapping: Mapping,
): FieldRow[] {
  const byField = new Map(rows.map(row => [row.field, row]));
  const known = new Set(mapping.headers ?? []);
  const automatic = new Map((mapping.entries ?? [])
    .filter(entry => entry.header !== null)
    .map(entry => [entry.field, entry.header as string]));

  /**
   * A row on the system's own choice reads from the system's own column,
   * whatever it has been pointed at since - and cannot sit on that choice at
   * all where there was none to make.
   */
  const resolve = (row: FieldRow): FieldRow =>
    row.source !== 'default' ? row
      : row.auto === null
        ? { ...row, source: 'blank', header: null }
        : { ...row, header: row.auto };

  /**
   * THE COLUMN A FIELD IS STILL POINTED AT while its source is something
   * else. Choosing a column, trying Blank, and coming back used to land on an
   * empty picker, because a field whose source is not `column` is absent from
   * the mapping and the row was rebuilt from the mapping alone. The column is
   * held on the row instead; nothing posts it while the source is not
   * `column`, so remembering it costs nothing until it is chosen again.
   */
  const remembered = (previous: FieldRow | undefined): Pick<FieldRow, 'header' | 'how'> =>
    previous && previous.header !== null && known.has(previous.header)
      ? { header: previous.header, how: previous.how }
      : { header: null, how: null };

  const writable = schema.fields
    .filter(field => field.writable)
    .map((field): FieldRow => {
      const row = byField.get(field.name);
      const header = row?.header ?? null;
      const auto = automatic.get(field.name) ?? null;
      const previous = held.get(field.name);
      const base: FieldRow = {
        field: field.name,
        label: field.label,
        required: field.required,
        defaultValue: field.defaultValue ?? null,
        how: header !== null ? row?.how ?? null : null,
        source: 'blank',
        header,
        auto,
        value: previous?.value ?? '',
        overridable: false,
        recalculated: false,
      };

      // An answer in progress survives the feedback loop. Choosing 'a column
      // of your file' and not yet picking which one emits an empty mapping,
      // the dialog hands it back, and rebuilding from scratch here would snap
      // the row to blank while the person is mid-decision - the picker they
      // just opened would vanish under their pointer.
      //
      // It is also the ONLY thing that can tell the system's column from the
      // same column picked by hand: they are one message once posted, so the
      // mapping alone cannot say which of the two a person is looking at.
      if (previous) {
        return resolve({
          ...base,
          source: previous.source,
          value: previous.value,
          ...remembered(previous),
        });
      }

      // Nothing has been touched yet, so a column in the mapping IS the
      // system's answer, and the row says so by sitting on Default.
      if (header !== null)
        return resolve({ ...base, source: auto === header ? 'default' : 'column' });

      return { ...base, source: 'blank' };
    });

  const overridable = schema.fields
    .filter(field => !field.writable && field.overridable === true)
    .map((field): FieldRow => {
      const header = settled ? choice[field.name] ?? null : null;
      const previous = held.get(field.name);
      const kept = remembered(previous);
      return resolve({
        field: field.name,
        label: field.label,
        required: false,
        defaultValue: null,
        // The system never matches a read-only field, so there is no
        // automatic answer to go back to and Default has nothing to offer.
        auto: null,
        how: header !== null ? 'chosen' : kept.how,
        // The same rule as the writable rows above, and for the same reason.
        // Choosing "A column" and not yet saying WHICH emits a mapping this
        // field is absent from; the dialog hands that straight back, and a
        // row rebuilt from the mapping alone lands on 'blank' - so the picker
        // never appears and the row seems to refuse the choice. This branch
        // only ever honoured a held `value`, which left the read-only rows -
        // the ones the unused-columns list sends people to - unable to be
        // pointed at anything.
        source: header !== null ? 'column' : previous?.source ?? 'blank',
        header: header ?? kept.header,
        value: previous?.value ?? '',
        overridable: true,
        recalculated: field.recalculated === true,
      });
    });

  return [...writable, ...overridable];
}

/**
 * THE FILE\'S COLUMNS NOTHING READS, for the marked list at the bottom.
 * The other half of the mismatch: a column of theirs that no field of ours
 * takes, said in the same colour the unfed fields wear.
 */
export interface UnusedColumn {
  header: string;
  /** Our name for it, when we recognise it and will not write it. */
  ourLabel: string | null;
  computed: boolean;
  /** Whether choosing it for a field above is actually possible. */
  overridable: boolean;
  /** True = we have never heard of this column. */
  unknown: boolean;
}

export function unusedColumns(
  headers: readonly string[],
  fields: readonly FieldRow[],
  unwritable: readonly UnwritableColumn[],
  schema: TransferSchema,
): UnusedColumn[] {
  const used = new Set(fields
    .filter(readsAColumn)
    .map(row => row.header as string));
  const held = new Map(unwritable.map(column => [column.header, column]));
  const labels = new Map(schema.fields.map(field => [field.name, field.label]));
  const offered = new Set(schema.fields
    .filter(field => !field.writable && field.overridable === true)
    .map(field => field.name));

  return headers
    .filter(header => !used.has(header))
    .map((header): UnusedColumn => {
      const cannot = held.get(header);
      if (cannot) {
        return {
          header,
          ourLabel: labels.get(cannot.field) ?? cannot.field,
          computed: cannot.computed,
          overridable: offered.has(cannot.field),
          unknown: false,
        };
      }
      return {
        header, ourLabel: null, computed: false,
        overridable: false, unknown: true,
      };
    });
}

/** The value a select carries for "this column feeds no field". */
export const NO_FIELD = '';

/** The value a select carries for "this field has no column". */
export const NO_COLUMN = '';

/**
 * Every field the entity can be written through, whether or not the mapping
 * mentioned it: a field the file never named is exactly the one somebody may
 * need to point at a column, and walking the entries alone would hide it.
 *
 * Once anything has been settled by hand, the choice IS the mapping. Reading
 * it together with the inferred entries would let a cleared column reappear
 * from the inference it was cleared in favour of nothing.
 */
export function mappingRows(
  schema: TransferSchema,
  mapping: Mapping,
  choice: Record<string, string>,
  settled: boolean,
): MappingRow[] {
  const entries = new Map<string, MappingEntry>(
    mapping.entries.map(entry => [entry.field, entry]));
  return schema.fields
    .filter(field => field.writable)
    .map(field => {
      const entry = entries.get(field.name);
      const header = settled
        ? choice[field.name] ?? null
        : entry?.header ?? null;
      return {
        field: field.name,
        label: field.label,
        required: field.required,
        writable: field.writable,
        how: howFor(entry, header),
        header,
      };
    });
}

/**
 * A required field reading from no column does not produce a few bad rows. It
 * fails EVERY row in the file, all for the same reason, which is a thing to
 * find out before the file is read rather than from a plan that is entirely
 * red.
 */
export function requiredWithoutColumn(rows: readonly MappingRow[]): MappingRow[] {
  return rows.filter(row => row.required && row.header === null);
}

/**
 * A column matching what was inferred keeps the inference's standing, so
 * re-picking the same column does not turn an exact match into a claim that
 * somebody decided it. Anything else is a decision; nothing is unmatched.
 */
function howFor(
  entry: MappingEntry | undefined,
  header: string | null,
): MappingHow {
  if (header === null) return 'unmatched';
  if (entry && entry.header === header) return entry.how;
  return 'chosen';
}
